import {
  Component,
  EventEmitter,
  OnDestroy,
  OnChanges,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  catchError,
  of,
  interval,
  startWith,
  Subscription,
  switchMap,
  filter,
} from 'rxjs';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { S3UploadService } from 'src/app/core/services/s3-upload/s3-upload.service';
import { S3BucketName } from 'src/app/shared/models/constants/s3-bucket-name';
import {
  MigrationUploadFile,
  MigrationUploadFileStatus,
  MigrationUploadFileSupplyType,
} from 'src/app/shared/models/migration-upload-file/migration-upload-file';
import { oneSecond } from 'src/app/utils/time';

type TStatusColorClass = {
  red: boolean;
  green: boolean;
};

@Component({
  selector: 'app-upload-queue-head',
  templateUrl: './upload-queue-head.component.html',
  styleUrls: ['./upload-queue-head.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Upload Queue Head Component! :)
 */
export class UploadQueueHeadComponent implements OnDestroy, OnChanges {
  fetchedFiles: MigrationUploadFile[] = [];
  moreFilesAvailable = false;
  queueHeadLimit = 5; // 5 files
  loading: boolean = true;
  call?: Subscription;
  @Input() type: any;
  @Output() retry = new EventEmitter<MigrationUploadFile>();
  @Output() downloadReport = new EventEmitter<MigrationUploadFile>();
  private readonly subsink = new Subscription();

  labels = {
    file: 'file',
    type: 'type',
    status: 'status',
    date: 'date',
    emptyQueue: 'empty-upload-queue-head',
    checkUploadHistory: 'click-here-check-upload-queue-history',
    retry: 'retry',
    cancel: 'cancel',
    report: 'report',
    download: 'download',
  };

  /**
   * @constructor
   */
  constructor(
    private readonly s3UploadService: S3UploadService,
    private readonly authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   *
   * @param {any} simpleChange
   */
  ngOnChanges(simpleChange: any) {
    this.fetchedFiles = [];
    if (this.call) {
      this.call.unsubscribe();
    }
    this.loading = true;
    const call$ = interval(30 * oneSecond)
      .pipe(
        startWith(0),
        switchMap(() => {
          const regionIds = this.authenticatedUserService.admin?.regions.map(
            (region) => region.id
          );
          if (regionIds === undefined) {
            // TODO: log unexpected undefined region
            return of(undefined);
          }
          return this.s3UploadService
            .getUploadQueue(0, 5, regionIds, simpleChange.type.currentValue)
            .pipe(
              catchError(() => {
                return of(undefined);
              })
            );
        }),
        filter((migrationFiles) => migrationFiles !== undefined)
      )
      .subscribe((migrationFiles?: MigrationUploadFile[]) => {
        this.s3UploadService.currentlyUploading.forEach(
          (migrationFile, index) => {
            if (migrationFile.status === MigrationUploadFileStatus.UPLOADED) {
              this.s3UploadService.currentlyUploading.splice(index, 1);
            }
          }
        );
        const uploadingIds = this.s3UploadService.currentlyUploading
          .map((migrationFile) => migrationFile.requestId)
          .filter((id) => id !== undefined);

        this.fetchedFiles = migrationFiles!
          .slice(0, this.queueHeadLimit)
          .filter(
            (migrationFile) =>
              migrationFile.requestId &&
              !uploadingIds.includes(migrationFile.requestId)
          );

        if (this.type === S3BucketName.PORT_REPORT) {
          this.fetchedFiles.forEach((file) => {
            if (file.status !== MigrationUploadFileStatus.PROCESSING_ERROR) {
              file.status = MigrationUploadFileStatus.PROCESSED;
            }
          });
        }

        if (migrationFiles!.length > this.queueHeadLimit) {
          this.moreFilesAvailable = true;
        }
        this.loading = false;
      });
    this.call = call$;
    this.subsink.add(call$);
  }

  /**
   * on destroy
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * A view of uploading + uploaded files
   */
  get files() {
    return [...this.s3UploadService.currentlyUploading]
      .reverse()
      .concat(this.fetchedFiles)
      .slice(0, this.queueHeadLimit);
  }

  /**
   * If files are available or not
   */
  get hasFiles() {
    return this.files.length > 0;
  }

  /**
   * @param {MigrationUploadFile} migrationFile
   * @return {TStatusColorClass}
   */
  fileStatusClass(migrationFile: MigrationUploadFile): TStatusColorClass {
    const status = migrationFile.status;

    return {
      green: status === MigrationUploadFileStatus.PROCESSED,
      red:
        status === MigrationUploadFileStatus.UPLOAD_ERROR ||
        status === MigrationUploadFileStatus.PROCESSING_ERROR ||
        status === MigrationUploadFileStatus.UPLOAD_PROCESSING_ERROR ||
        status === MigrationUploadFileStatus.PROCESSED_WITH_ERROR,
    };
  }

  /**
   * @param {MigrationUploadFileSupplyType} supplyType
   * @return {string}
   */
  fileSupplyTypeText(supplyType: MigrationUploadFileSupplyType): string {
    return `${supplyType.toLocaleLowerCase()}-supplies`;
  }

  /**
   * @param {MigrationUploadFileStatus} status
   * @return {string}
   */
  fileStatusText(status: MigrationUploadFileStatus): string {
    return status.toLocaleLowerCase().replace(/_/g, '-');
  }

  /**
   * @param {MigrationUploadFile} migrationFile
   * @return {boolean}
   */
  hasProgress(migrationFile: MigrationUploadFile): boolean {
    return Boolean(
      (migrationFile.status === MigrationUploadFileStatus.PROCESSING ||
        migrationFile.status === MigrationUploadFileStatus.PENDING) &&
        migrationFile.progress &&
        !isNaN(migrationFile.progress)
    );
  }

  /**
   * Emits retry request
   * @param {MigrationUploadFile} migrationFile
   */
  _retry(migrationFile: MigrationUploadFile) {
    if (this.canRetry(migrationFile)) {
      this.retry.emit(migrationFile);
    }
  }

  /**
   * Cancels upload
   * @param {MigrationUploadFile} migrationFile
   */
  cancel(migrationFile: MigrationUploadFile) {
    migrationFile.upload$?.unsubscribe();
    migrationFile.status = MigrationUploadFileStatus.CANCELED;
  }

  /**
   * Cancels upload
   * @param {MigrationUploadFile} migrationFile
   * @return {boolean}
   */
  canRetry(migrationFile: MigrationUploadFile): boolean {
    return (
      (migrationFile.status === MigrationUploadFileStatus.UPLOAD_ERROR ||
        migrationFile.status === MigrationUploadFileStatus.CANCELED) &&
      migrationFile.file !== undefined
    );
  }

  /**
   * Cancels upload
   * @param {MigrationUploadFile} migrationFile
   * @return {boolean}
   */
  canCancel(migrationFile: MigrationUploadFile): boolean {
    return (
      this.hasProgress(migrationFile) &&
      migrationFile.upload$ !== undefined &&
      migrationFile.progress !== 100
    );
  }

  /**
   * If a report can be downloaded
   * @param {MigrationUploadFile} migrationFile
   * @return {boolean}
   */
  canDownloadReport(migrationFile: MigrationUploadFile): boolean {
    return (
      (!migrationFile.downloading &&
        migrationFile.status ===
          MigrationUploadFileStatus.PROCESSED_WITH_ERROR) ||
      (!migrationFile.downloading &&
        migrationFile.dataType === S3BucketName.PORT_REPORT &&
        migrationFile.status === MigrationUploadFileStatus.PROCESSED)
    );
  }

  /**
   * Emits download report event
   * @param {MigrationUploadFile} migrationFile
   */
  emitDownloadReport(migrationFile: MigrationUploadFile) {
    this.downloadReport.emit(migrationFile);
  }
}
